/**
 * Pedestal absorb VFX + transform juice (hitstop / FOV punch / spawn flash).
 * Soldiers stream/beam into the pedestal (consume-N read); white flash at
 * solidify. Greybox only — no tone art. Driven by sim events; no sim logic here.
 */
import * as THREE from "three";
/** GDD juice: short freeze at transform chorus. */
export const TRANSFORM_HITSTOP_SECONDS = 0.14;
/** FOV punch: camera FOV widens then settles (degrees). */
export const TRANSFORM_FOV_PUNCH_DEG = 10;
/** How long FOV punch eases out. */
export const TRANSFORM_FOV_RECOVER_SECONDS = 0.42;
/** Absorb ring lifetime after transform event. */
export const ABSORB_DURATION_SECONDS = 0.55;
/** Pooled soldier→pedestal absorb beams (bounded). */
export const ABSORB_BEAM_POOL = 16;
/** White flash peak opacity at transform solidify. */
export const TRANSFORM_FLASH_OPACITY = 0.85;
export function createTransformJuiceState(baseFov = 48) {
    return {
        hitstopRemaining: 0,
        fovPunchT: 0,
        absorbAge: null,
        absorbZ: 0,
        absorbX: 0,
        mechaSpawned: false,
        baseFov,
    };
}
/**
 * Fire transform juice (call once on sim `transform` event).
 */
export function triggerTransformJuice(state, centroidX, pedestalZ) {
    state.hitstopRemaining = TRANSFORM_HITSTOP_SECONDS;
    state.fovPunchT = 1;
    state.absorbAge = 0;
    state.absorbX = centroidX;
    state.absorbZ = pedestalZ;
    state.mechaSpawned = true;
}
/**
 * Advance juice timers by display-frame dt. Returns effective sim dt scale (0 during hitstop).
 */
export function advanceTransformJuice(state, dtSeconds) {
    const dt = Math.max(0, dtSeconds);
    if (state.hitstopRemaining > 0) {
        state.hitstopRemaining = Math.max(0, state.hitstopRemaining - dt);
        // Still advance FOV/absorb for visual continuity during freeze
        if (state.fovPunchT > 0) {
            state.fovPunchT = Math.max(0, state.fovPunchT - dt / TRANSFORM_FOV_RECOVER_SECONDS);
        }
        if (state.absorbAge !== null) {
            state.absorbAge += dt;
            if (state.absorbAge >= ABSORB_DURATION_SECONDS) {
                state.absorbAge = null;
            }
        }
        return 0;
    }
    if (state.fovPunchT > 0) {
        state.fovPunchT = Math.max(0, state.fovPunchT - dt / TRANSFORM_FOV_RECOVER_SECONDS);
    }
    if (state.absorbAge !== null) {
        state.absorbAge += dt;
        if (state.absorbAge >= ABSORB_DURATION_SECONDS) {
            state.absorbAge = null;
        }
    }
    return 1;
}
/** Current camera FOV with punch applied (ease-out). */
export function currentFov(state) {
    if (state.fovPunchT <= 0)
        return state.baseFov;
    // Ease-out cubic on punch envelope
    const t = state.fovPunchT;
    const ease = t * t * (3 - 2 * t);
    return state.baseFov + TRANSFORM_FOV_PUNCH_DEG * ease;
}
const RING_COUNT = 4;
const SPARK_COUNT = 12;
/**
 * Greybox absorb: soldier beams into pedestal + expanding rings + white flash.
 * Count-drain read for consume-N transform chorus.
 */
export function createAbsorbVfx() {
    const group = new THREE.Group();
    group.name = "absorbVfx";
    group.visible = false;
    const ringGeo = new THREE.TorusGeometry(1, 0.06, 6, 24);
    const rings = [];
    for (let i = 0; i < RING_COUNT; i++) {
        const mat = new THREE.MeshBasicMaterial({
            color: 0x93c5fd,
            transparent: true,
            opacity: 0.7,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
        const mesh = new THREE.Mesh(ringGeo, mat);
        mesh.rotation.x = Math.PI / 2;
        mesh.name = `absorbRing:${i}`;
        group.add(mesh);
        rings.push(mesh);
    }
    const columnGeo = new THREE.CylinderGeometry(0.35, 0.55, 3.2, 8, 1, true);
    const columnMat = new THREE.MeshBasicMaterial({
        color: 0xcbd5e1,
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
    });
    const column = new THREE.Mesh(columnGeo, columnMat);
    column.name = "absorbColumn";
    column.position.y = 1.6;
    group.add(column);
    const sparkGeo = new THREE.SphereGeometry(0.08, 6, 4);
    const sparks = [];
    for (let i = 0; i < SPARK_COUNT; i++) {
        const mat = new THREE.MeshBasicMaterial({
            color: i % 2 === 0 ? 0x60a5fa : 0xe2e8f0,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
        const mesh = new THREE.Mesh(sparkGeo, mat);
        mesh.name = `absorbSpark:${i}`;
        group.add(mesh);
        sparks.push(mesh);
    }
    // Pooled absorb beams: thin cylinders from soldiers → pedestal center
    const beamGeo = new THREE.CylinderGeometry(0.04, 0.02, 1, 5, 1);
    // Default cylinder is Y-up; we orient via quaternion each frame
    beamGeo.translate(0, 0.5, 0); // origin at start (soldier end)
    const beams = [];
    for (let i = 0; i < ABSORB_BEAM_POOL; i++) {
        const mat = new THREE.MeshBasicMaterial({
            color: i % 2 === 0 ? 0x7dd3fc : 0xe0f2fe,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
        const mesh = new THREE.Mesh(beamGeo, mat);
        mesh.name = `absorbBeam:${i}`;
        mesh.visible = false;
        group.add(mesh);
        beams.push(mesh);
    }
    // White flash at solidify (world-space sphere; big enough to punch the frame)
    const flashGeo = new THREE.SphereGeometry(1.4, 14, 10);
    const flashMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const flash = new THREE.Mesh(flashGeo, flashMat);
    flash.name = "absorbFlash";
    flash.position.y = 1.6;
    group.add(flash);
    // Full-scene white bloom disc (flat, camera-facing-ish plate on ground)
    const bloomGeo = new THREE.CircleGeometry(3.2, 20);
    const bloomMat = new THREE.MeshBasicMaterial({
        color: 0xf8fafc,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
    });
    const bloom = new THREE.Mesh(bloomGeo, bloomMat);
    bloom.name = "absorbWhiteFlash";
    bloom.rotation.x = -Math.PI / 2;
    bloom.position.y = 0.08;
    group.add(bloom);
    const _dir = new THREE.Vector3();
    const _up = new THREE.Vector3(0, 1, 0);
    const _quat = new THREE.Quaternion();
    const placeBeam = (mesh, fromX, fromY, fromZ, toX, toY, toZ, opacity) => {
        _dir.set(toX - fromX, toY - fromY, toZ - fromZ);
        const len = _dir.length();
        if (len < 0.05 || opacity <= 0.01) {
            mesh.visible = false;
            return;
        }
        mesh.visible = true;
        mesh.position.set(fromX, fromY, fromZ);
        mesh.scale.set(1, len, 1);
        _dir.normalize();
        _quat.setFromUnitVectors(_up, _dir);
        mesh.quaternion.copy(_quat);
        mesh.material.opacity = opacity;
    };
    const update = (activeOrOpts, ageSeconds = 0, x = 0, z = 0) => {
        let active;
        let age;
        let px;
        let pz;
        let origins;
        let originCount = 0;
        let drainN = 0;
        if (typeof activeOrOpts === "object") {
            active = activeOrOpts.active;
            age = activeOrOpts.ageSeconds;
            px = activeOrOpts.x;
            pz = activeOrOpts.z;
            origins = activeOrOpts.beamOrigins;
            originCount =
                activeOrOpts.beamOriginCount ??
                    (origins !== undefined ? origins.length : 0);
            drainN = activeOrOpts.drainN ?? 0;
        }
        else {
            active = activeOrOpts;
            age = ageSeconds;
            px = x;
            pz = z;
        }
        if (!active) {
            group.visible = false;
            return;
        }
        group.visible = true;
        // Beams use world-space origins relative to group at pedestal
        group.position.set(px, 0, pz);
        const u = Math.max(0, Math.min(1, age / ABSORB_DURATION_SECONDS));
        // Inward pull then bloom (consume-N read)
        const pull = u < 0.45 ? u / 0.45 : 1 - (u - 0.45) / 0.55;
        for (let i = 0; i < RING_COUNT; i++) {
            const mesh = rings[i];
            const phase = (i + 1) / RING_COUNT;
            const scale = 0.4 + phase * 2.2 * (1 - pull * 0.65) + u * 0.4;
            mesh.scale.setScalar(scale);
            mesh.position.y = 0.3 + i * 0.35 + pull * 0.8;
            mesh.material.opacity =
                0.15 + (1 - u) * 0.65 * (1 - phase * 0.2);
        }
        column.scale.set(1 + pull * 0.4, 1 + pull * 0.5, 1 + pull * 0.4);
        columnMat.opacity = 0.15 + (1 - u) * 0.45;
        for (let i = 0; i < SPARK_COUNT; i++) {
            const mesh = sparks[i];
            const a = (i / SPARK_COUNT) * Math.PI * 2 + age * 6;
            const r = (1.6 - pull * 1.2) * (0.5 + (i % 3) * 0.25);
            const y = 0.4 + ((i * 0.37 + age * 2.5) % 2.8);
            mesh.position.set(Math.cos(a) * r, y, Math.sin(a) * r);
            mesh.material.opacity = (1 - u) * 0.9;
        }
        // Soldier stream/beams into pedestal (local space: origins relative to pedestal)
        const validOrigins = origins && originCount > 0 ? originCount : 0;
        const beamCount = validOrigins > 0
            ? Math.min(ABSORB_BEAM_POOL, validOrigins)
            : Math.min(ABSORB_BEAM_POOL, Math.max(6, Math.min(ABSORB_BEAM_POOL, drainN > 0 ? Math.ceil(drainN / 2) : 10)));
        const beamOp = (1 - u * 0.85) * (0.45 + pull * 0.5);
        for (let i = 0; i < ABSORB_BEAM_POOL; i++) {
            const mesh = beams[i];
            if (i >= beamCount) {
                mesh.visible = false;
                continue;
            }
            let ox;
            let oy;
            let oz;
            if (origins && validOrigins > 0) {
                const o = origins[i % validOrigins];
                // Convert world → local (group is at pedestal)
                ox = o.x - px;
                oy = o.y ?? 0.9;
                oz = o.z - pz;
            }
            else {
                // Procedural ring of inbound soldiers when origins not supplied
                const a = (i / beamCount) * Math.PI * 2 + age * 2.4;
                const r = 2.2 + (i % 3) * 0.55 + (1 - pull) * 1.4;
                ox = Math.cos(a) * r;
                oy = 0.7 + (i % 4) * 0.15;
                oz = Math.sin(a) * r * 0.55 - 0.8;
            }
            // Pull origin toward center as absorb progresses
            const shrink = 1 - pull * 0.75;
            ox *= shrink;
            oz *= shrink;
            oy = oy * (1 - pull * 0.4) + 0.5 * pull;
            // Beam target: pedestal pad center
            placeBeam(mesh, ox, oy, oz, 0, 1.1 + pull * 0.6, 0, beamOp);
        }
        // White flash peaks at solidify mid-point then dies
        const flashPeak = Math.sin(Math.min(1, u * 1.85) * Math.PI);
        flash.scale.setScalar(0.6 + flashPeak * 2.4);
        flashMat.opacity = flashPeak * TRANSFORM_FLASH_OPACITY * (1 - u * 0.25);
        bloom.scale.setScalar(0.7 + flashPeak * 1.6);
        bloomMat.opacity = flashPeak * 0.55 * (1 - u * 0.4);
    };
    const dispose = () => {
        ringGeo.dispose();
        columnGeo.dispose();
        sparkGeo.dispose();
        flashGeo.dispose();
        bloomGeo.dispose();
        beamGeo.dispose();
        columnMat.dispose();
        flashMat.dispose();
        bloomMat.dispose();
        for (const mesh of rings) {
            mesh.material.dispose();
        }
        for (const mesh of sparks) {
            mesh.material.dispose();
        }
        for (const mesh of beams) {
            mesh.material.dispose();
        }
        group.clear();
    };
    return { group, update, dispose };
}
/**
 * Apply FOV punch to a perspective camera (mutates fov + projection).
 */
export function applyCameraFov(camera, fov) {
    if (Math.abs(camera.fov - fov) < 0.01)
        return;
    camera.fov = fov;
    camera.updateProjectionMatrix();
}
//# sourceMappingURL=transformFx.js.map